import { ApolloError, useApolloClient } from "@apollo/client";
import * as React from "react";
import { IWizardFormProps } from "../../../components/Wizard";
import { WizardContext } from "../../../components/Wizard/Context";
import { AdvancedEmailWizardContext, ErrorContext } from "../../../context";
import { ISendEmailDataModel } from "../../EmailWizard/models/SendEmail";
import { ITemplateDataModel } from "../../TemplateWizard/models/Template";
import { AttachmentInput } from "../Attachments/AttachmentInput";
import { ExecuteModel } from "../ExecuteModel";
import { IEmailPreview } from "../Preview/graphQL/emailPreview";
import { AttachmentInputDiv } from "./EmailContent.styles";
import { EmailEditor } from "./EmailEditor";
import { EmailScribanValidationErrors } from "./EmailValidationErrors/EmailScribanValidationErrors";
import { ScribanValidatorService } from "../../../data/graphQL/services/ScribanValidatorService";

export const EmailContent = <T extends ExecuteModel<ITemplateDataModel | ISendEmailDataModel>>({ FormikProps, IsShown, SetHasValidationErrors, ShouldValidate }: IWizardFormProps<T>) => {
    // Context
    const advancedEmailWizardContext = React.useContext(AdvancedEmailWizardContext);
    const { SubmitWizard } = React.useContext(WizardContext);
    const errorContext = React.useContext(ErrorContext);

    // GraphQL
    const apolloClient = useApolloClient();
    const scribanValidatorService = React.useMemo(() => new ScribanValidatorService(apolloClient, advancedEmailWizardContext), []);

    // State
    const [emailPreviewResponse, setEmailPreviewResponse] = React.useState<IEmailPreview>();

    const [isLoading, setIsLoading] = React.useState(true);
    const handleOnLoad = () => {
        setIsLoading(false);
    };

    const [showModal, setShowModal] = React.useState(false);
    const showErrorModal = (show: boolean) => {
        setShowModal(show);
    };

    /*
        The following effect is triggered when the Wizard is submitting on the current step.
        We make a call to the validation endpoint and do one of the following:
            1. If errors are returned from the request, a modal appears with the errors, fieldErrors are set on the
               email body to disable the submit button, HasValidationErrors callback returns with true to stop the
               wizard from progressing to the next step.
            2. If there are no errors returned, we set the email body and subject to the values returned from the response
               and set the HasValidationErrors callback to false which allows the wizard to progress to the next step
    */
    React.useEffect(() => {
        if (FormikProps?.isSubmitting && IsShown) {
            if (ShouldValidate) {
                (async () => scribanValidatorService.ValidateEmailContent(FormikProps?.values)
                    .then((response) => {
                        setEmailPreviewResponse({
                            body: response.body,
                            subject: response.subject,
                            errors: response.errors,
                        });

                        FormikProps?.setSubmitting(false);

                        if (response.errors) {
                            setShowModal(true);
                        }

                    }).catch((error: ApolloError) => {
                        errorContext.SetError(error);
                    })
                )();
            } else {
                FormikProps?.setFieldValue(nameof.full<T>((m) => m.formDataModel.Preview), {
                    body: FormikProps.values.formDataModel.EmailBody,
                    subject: FormikProps.values.formDataModel.EmailSubject,
                });
            }
        }
    }, [FormikProps?.isSubmitting]);

    React.useEffect(() => {
        if (!IsShown) {
            FormikProps?.setErrors({});
        }
    }, [IsShown]);

    React.useEffect(() => {
        if (!IsShown) {
            FormikProps?.setErrors({});
        }
    }, [IsShown]);

    React.useEffect(() => {
        if (emailPreviewResponse) {
            if (emailPreviewResponse.errors?.length! > 0) {
                SetHasValidationErrors!(true);
                FormikProps?.setFieldError(nameof.full<T>((m) => m.formDataModel.EmailBody), "Scriban Validation Error");
            } else {
                SetHasValidationErrors!(false);
                FormikProps?.setFieldValue(nameof.full<T>((m) => m.formDataModel.Preview), emailPreviewResponse);
                SubmitWizard!();
            }
        }
    }, [emailPreviewResponse]);

    return (
        <>
            { showModal && emailPreviewResponse?.errors && emailPreviewResponse.errors?.length > 0 ?
                <EmailScribanValidationErrors Errors={emailPreviewResponse.errors!} OnClose={() => showErrorModal(false)} />
                : null
            }

            <EmailEditor
                FormikProps={FormikProps}
                OnLoad={handleOnLoad}
                IsShown={IsShown!}
            />
            {!isLoading &&
                <>
                    <AttachmentInputDiv>
                        <AttachmentInput FormikProps={FormikProps} />
                    </AttachmentInputDiv>
                </>
            }
        </>
    );
};
